/**
 * Inventory List Component
 * Main inventory list tab with header, filters, and table
 */

import React, { useState, useMemo, useEffect, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { usePermissions } from '../../../utils/permissions';
import {
  Box, Typography, Button, Grid, TextField, InputAdornment, Paper, Chip,
  Tooltip, Stack, useTheme, alpha, Select, MenuItem, IconButton, Fab,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination
} from '@mui/material';
import {
  Search as SearchIcon, Add as AddIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Cancel as CancelIcon, Tune as TuneIcon, ExpandMore as ExpandMoreIcon,
  Close as CloseIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { getInventoryConfig } from './shared/inventoryConfig';
import { useInventoryData, useInventoryItems, useLocations, useSuppliers } from './shared/useInventoryData';
import InventoryTable from './shared/InventoryTable';
import ChemicalTable from './shared/inventory-table-components/ChemicalTable';
import ProductTable from './shared/inventory-table-components/ProductTable';
import InventoryAdjustments from './InventoryAdjustments';
import AddInventoryStepper from './Add/AddInventoryStepper';
import TransferInventoryStepper from './Transfer/TransferInventoryStepper/TransferInventoryStepper';
import UsageInventoryStepper from './Usage/UsageInventoryStepper/UsageInventoryStepper';
import FilterDialogs from './shared/FilterDialogs';
import StatCards from './shared/StatCards';
import PaginationController from './shared/PaginationController';
import { exportInventoryReport } from './shared/exportInventoryReport';
import AllInventoryView from './all-inventory/AllInventoryView';

export default function InventoryList({ companyId, inventoryType, setInventoryType, onShowSnackbar }) {
  const theme = useTheme();
  const { companyId: routeCompanyId } = useParams();
  const { canEditInventoryPage, canDeleteInventoryPage } = usePermissions();
  const canEdit   = canEditInventoryPage();
  const canDelete = canDeleteInventoryPage();
  const cid = companyId || routeCompanyId;

  // Guard for "All Inventory" — never pass 'all' to hooks that call getInventoryConfig
  const isAll    = inventoryType === 'all';
  const safeType = isAll ? 'caps' : inventoryType;
  const safeCid  = isAll ? null   : companyId;

  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialog states
  const [adjustmentsDialogOpen, setAdjustmentsDialogOpen] = useState(false);
  const [addStepperOpen, setAddStepperOpen] = useState(false);
  const [transferStepperOpen, setTransferStepperOpen] = useState(false);
  const [usageStepperOpen, setUsageStepperOpen] = useState(false);
  const [locationFilterDialogOpen, setLocationFilterDialogOpen] = useState(false);
  const [itemFilterDialogOpen, setItemFilterDialogOpen] = useState(false);
  const [vendorFilterDialogOpen, setVendorFilterDialogOpen] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [vendorSearchTerm, setVendorSearchTerm] = useState('');
  const [dialogSelectedLocation, setDialogSelectedLocation] = useState(null);
  const [dialogSelectedItem, setDialogSelectedItem] = useState(null);
  const [dialogSelectedVendor, setDialogSelectedVendor] = useState(null);
  const [exportReportDialogOpen, setExportReportDialogOpen] = useState(false);
  const [reportType, setReportType] = useState('summary');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  // Brand filter state (products only)
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [brandFilterDialogOpen, setBrandFilterDialogOpen] = useState(false);
  const [brandFilterSearchTerm, setBrandFilterSearchTerm] = useState('');
  const [dialogSelectedBrandFilter, setDialogSelectedBrandFilter] = useState(null);
  const [brandSelectionMade, setBrandSelectionMade] = useState(false);

  // Per-brand pagination (products only)
  const [brandPages, setBrandPages] = useState({});
  const [brandPageSizes, setBrandPageSizes] = useState({});

  // Load data
  const { locations } = useLocations(companyId);
  const { suppliers } = useSuppliers(companyId);
  const { items } = useInventoryItems(safeType, safeCid);

  const primaryLocationId = (locations || []).find(l => l.is_main || l.is_primary)?.id || null;
  const { aggregatedData, loading, error, refresh } = useInventoryData(safeType, safeCid, { locationId: selectedLocation || null });

  const config = getInventoryConfig(safeType);
  const isProduct = inventoryType === 'products';

  // Fetch brands for product inventory
  const loadBrands = useCallback(async () => {
    if (!cid || !isProduct) return;
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name, display_order')
        .eq('company_id', cid)
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('name');
      if (!error) setBrands(data || []);
    } catch (err) {
      console.error('Error loading brands:', err);
    }
  }, [cid, isProduct]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Reset brand filter when inventory type changes
  useEffect(() => {
    setSelectedBrand('all');
    setBrandPages({});
    setBrandPageSizes({});
  }, [inventoryType]);

  // Brand filter helpers
  const getFilteredBrandsForFilter = () => {
    if (!brandFilterSearchTerm) return brands;
    return brands.filter(b => b.name.toLowerCase().includes(brandFilterSearchTerm.toLowerCase()));
  };

  const handleOpenBrandFilterDialog = () => {
    setBrandFilterDialogOpen(true);
    setBrandFilterSearchTerm('');
    setDialogSelectedBrandFilter(null);
    setBrandSelectionMade(false);
  };

  const handleCloseBrandFilterDialog = () => {
    setBrandFilterDialogOpen(false);
    setBrandFilterSearchTerm('');
    setDialogSelectedBrandFilter(null);
    setBrandSelectionMade(false);
  };

  const handleDialogBrandFilterSelect = (brand) => {
    setDialogSelectedBrandFilter(brand);
    setBrandSelectionMade(true);
  };

  const handleConfirmBrandFilterSelection = () => {
    if (dialogSelectedBrandFilter === 'all') {
      setSelectedBrand('all');
    } else if (dialogSelectedBrandFilter) {
      setSelectedBrand(dialogSelectedBrandFilter.id);
    } else {
      setSelectedBrand('all');
    }
    setBrandFilterDialogOpen(false);
    setBrandFilterSearchTerm('');
    setDialogSelectedBrandFilter(null);
    setBrandSelectionMade(false);
  };

  const getSelectedBrandName = () => {
    if (selectedBrand === 'all') return 'All Brands';
    const brand = brands.find(b => b.id === selectedBrand);
    return brand ? brand.name : 'Unknown Brand';
  };

  // Per-brand pagination helpers
  const getBrandPage = (brandId) => brandPages[brandId] || 1;
  const getBrandPageSize = (brandId) => brandPageSizes[brandId] || 10;

  const handleBrandPageChange = (brandId, newPage) => {
    setBrandPages(prev => ({ ...prev, [brandId]: newPage }));
  };

  const handleBrandRowsPerPageChange = (brandId, event) => {
    const newSize = parseInt(event.target.value, 10);
    setBrandPageSizes(prev => ({ ...prev, [brandId]: newSize }));
    setBrandPages(prev => ({ ...prev, [brandId]: 1 }));
  };

  const typeOptions = [
    { value: 'all',       label: 'All Inventory' },
    { value: 'products',  label: 'Products' },
    { value: 'chemicals', label: 'Chemicals' },
    { value: 'caps',      label: 'Caps' },
    { value: 'bottles',   label: 'Bottles' },
    { value: 'labels',    label: 'Labels' },
    { value: 'supplies',  label: 'Supplies' }
  ];

  const handleInventoryUpdate = async (message) => {
    onShowSnackbar(message);
    await refresh();
  };

  // Enrich aggregated data with brand_id for products
  const enrichedAggregatedData = useMemo(() => {
    if (!isProduct || !items || items.length === 0) return aggregatedData || [];
    return (aggregatedData || []).map(agg => {
      const product = items.find(p => p.id === agg.item_id);
      return { ...agg, brand_id: product?.brand_id || null };
    });
  }, [aggregatedData, items, isProduct]);

  // Filter inventory
  const filteredInventory = (isProduct ? enrichedAggregatedData : (aggregatedData || [])).filter(item => {
    const matchesSearch = item.item_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.item_sku?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = !selectedLocation || item.location_id === selectedLocation;
    const matchesItem = !selectedItem || item.item_id === selectedItem;
    const matchesVendor = !selectedSupplier || (item.lots && item.lots.some(lot =>
      lot.vendor_info?.supplier_id === selectedSupplier
    ));
    const matchesBrand = !isProduct || selectedBrand === 'all' || item.brand_id === selectedBrand;
    return matchesSearch && matchesLocation && matchesItem && matchesVendor && matchesBrand;
  });

  // When "All Locations": show all, but sort so primary location first
  const sortedInventory = React.useMemo(() => {
    if (selectedLocation || !primaryLocationId) return filteredInventory;
    return [...filteredInventory].sort((a, b) => {
      const aPrimary = a.location_id === primaryLocationId ? 1 : 0;
      const bPrimary = b.location_id === primaryLocationId ? 1 : 0;
      return bPrimary - aPrimary;
    });
  }, [filteredInventory, selectedLocation, primaryLocationId]);

  const handleApplyLocationFilter = () => {
    setSelectedLocation(dialogSelectedLocation || '');
    setLocationFilterDialogOpen(false);
  };

  const handleApplyItemFilter = () => {
    setSelectedItem(dialogSelectedItem || '');
    setItemFilterDialogOpen(false);
  };

  const handleApplyVendorFilter = () => {
    setSelectedSupplier(dialogSelectedVendor || '');
    setVendorFilterDialogOpen(false);
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalItems = filteredInventory.length;
    const inStock = filteredInventory.filter(item => (item.total_quantity || 0) > 0).length;
    const lowStock = filteredInventory.filter(item => {
      const qty = item.total_quantity || 0;
      const rp = item.reorder_point || 0;
      return rp > 0 && qty <= rp && qty > 0;
    }).length;
    const outOfStock = filteredInventory.filter(item => (item.total_quantity || 0) <= 0).length;
    return { totalItems, inStock, lowStock, outOfStock };
  }, [filteredInventory]);

  React.useEffect(() => {
    setPage(0);
  }, [sortedInventory.length]);

  const paginatedInventory = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedInventory.slice(start, start + rowsPerPage);
  }, [sortedInventory, page, rowsPerPage]);

  // Organize product inventory by brand
  const organizedByBrand = useMemo(() => {
    if (!isProduct) return [];
    const inventoryByBrand = {};
    sortedInventory.forEach(item => {
      const brandId = item.brand_id || 'unbranded';
      if (!inventoryByBrand[brandId]) inventoryByBrand[brandId] = [];
      inventoryByBrand[brandId].push(item);
    });

    const groups = Object.keys(inventoryByBrand).map(brandId => {
      const brand = brandId === 'unbranded'
        ? { id: 'unbranded', name: 'Unbranded Products' }
        : brands.find(b => b.id === brandId) || { id: brandId, name: 'Unknown Brand' };
      const brandInventory = inventoryByBrand[brandId];
      const currentPage = getBrandPage(brandId);
      const currentPageSize = getBrandPageSize(brandId);
      const paginatedItems = brandInventory.length <= currentPageSize
        ? brandInventory
        : brandInventory.slice((currentPage - 1) * currentPageSize, currentPage * currentPageSize);
      return {
        brand,
        inventory: brandInventory,
        paginatedInventory: paginatedItems,
        currentPage,
        currentPageSize
      };
    });
    groups.sort((a, b) => {
      const aOrder = (a.brand.display_order != null) ? a.brand.display_order : 9999;
      const bOrder = (b.brand.display_order != null) ? b.brand.display_order : 9999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.brand.name.localeCompare(b.brand.name);
    });
    return groups;
  }, [isProduct, sortedInventory, brands, brandPages, brandPageSizes]);

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleBrandReorder = async (brandId, direction) => {
    const groups = organizedByBrand;
    const currentIndex = groups.findIndex(g => g.brand.id === brandId);
    if (currentIndex === -1) return;
    if (direction === 'up' && currentIndex === 0) return;
    if (direction === 'down' && currentIndex === groups.length - 1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newGroups = [...groups];
    [newGroups[currentIndex], newGroups[targetIndex]] = [newGroups[targetIndex], newGroups[currentIndex]];

    // Optimistically update brands state with new display_order values
    const newBrands = [...brands];
    newGroups.forEach((g, index) => {
      if (g.brand.id !== 'unbranded') {
        const brand = newBrands.find(b => b.id === g.brand.id);
        if (brand) brand.display_order = index;
      }
    });
    setBrands([...newBrands]);

    // Persist to Supabase
    const updates = newGroups
      .filter(g => g.brand.id !== 'unbranded')
      .map((g, index) =>
        supabase.from('brands').update({ display_order: index }).eq('id', g.brand.id)
      );
    await Promise.all(updates);
  };

  // ── All Inventory early return (after all hooks) ──────────────────────────
  if (isAll) {
    return (
      <Fragment>
        <Box sx={{ p: 4 }}>
          {/* Type Selector Dropdown */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
              Inventory Type:
            </Typography>
            <Select
              value={inventoryType}
              onChange={(e) => {
                setInventoryType(e.target.value);
                setSearchTerm('');
                setSelectedLocation('');
                setSelectedItem('');
                setSelectedSupplier('');
              }}
              size="small"
              sx={{
                minWidth: 200, borderRadius: 3, bgcolor: 'white',
                '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` },
                '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` }
              }}
            >
              {typeOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </Box>

          <AllInventoryView
            companyId={cid}
            locations={locations}
            onShowSnackbar={onShowSnackbar}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        </Box>
      </Fragment>
    );
  }

  return (
    <Fragment>
    <Box sx={{ p: 4 }}>
      {/* Type Selector Dropdown */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
          Inventory Type:
        </Typography>
        <Select
          value={inventoryType}
          onChange={(e) => {
            setInventoryType(e.target.value);
            setSearchTerm('');
            setSelectedLocation('');
            setSelectedItem('');
            setSelectedSupplier('');
          }}
          size="small"
          sx={{
            minWidth: 200,
            borderRadius: 3,
            bgcolor: 'white',
            '& .MuiOutlinedInput-notchedOutline': {
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              border: `1px solid ${theme.palette.primary.main}`
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: `1px solid ${theme.palette.primary.main}`
            }
          }}
        >
          {typeOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: 3,
        p: 3,
        borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%',
          transform: 'translate(50%, -50%)',
          zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'success.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 }
              }
            }} />
            <Typography variant="caption" sx={{
              color: 'success.main',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontSize: '0.75rem'
            }}>
              Live Tracking
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4" sx={{
              fontWeight: 500,
              color: 'text.primary',
              fontSize: '1.8rem',
              lineHeight: 1.2
            }}>
              {config.displayName} Inventory
            </Typography>
          </Box>

          <Typography variant="body2" sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.9rem',
            mb: 2.5,
            maxWidth: '400px'
          }}>
            Real-time inventory tracking with lot management, supplier documentation, and reorder alerts
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${filteredInventory.length} Items`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.info.main, 0.1),
                color: 'info.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}
            />
            <Chip
              label={`${filteredInventory.filter(item => (item.total_quantity || 0) > 0).length} In Stock`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.success.main, 0.1),
                color: 'success.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
              }}
            />
            <Chip
              label={`${filteredInventory.filter(item => {
                const qty = item.total_quantity || 0;
                const rp = item.reorder_point || 0;
                return rp > 0 && qty <= rp && qty > 0;
              }).length} Low Stock`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
                fontWeight: 500,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Tooltip title="Refresh inventory data">
            <IconButton
              onClick={refresh}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: 'primary.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  transform: 'rotate(180deg)',
                  transition: 'all 0.4s ease'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => setExportReportDialogOpen(true)}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontSize: '0.9rem',
              fontWeight: 500,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              color: 'text.primary',
              '&:hover': {
                bgcolor: alpha(theme.palette.grey[100], 0.8),
                border: `1px solid ${alpha(theme.palette.grey[600], 0.8)}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Export Report
          </Button>

          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={() => setAdjustmentsDialogOpen(true)}
            disabled={!canEdit}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              fontSize: '0.9rem',
              fontWeight: 500,
              bgcolor: 'black',
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.8)',
                transform: 'translateY(-1px)'
              },
              '&.Mui-disabled': {
                bgcolor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            Manage Inventory
          </Button>
        </Box>
      </Box>

      {/* Stat Cards */}
      <StatCards filteredInventory={filteredInventory} config={config} />

      {/* Filters */}
      <Paper sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
        boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
        }
      }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="body2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Search
              </Typography>
              <TextField
                fullWidth
                placeholder="Search inventory..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    height: 56,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                    },
                    '&.Mui-focused': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                    }
                  }
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    py: 2,
                    px: 2
                  },
                  '& .MuiOutlinedInput-root': {
                    height: 56,
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  }
                }}
                size="medium"
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="body2" sx={{
                mb: 1,
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.9rem'
              }}>
                Location
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { setDialogSelectedLocation(selectedLocation); setLocationFilterDialogOpen(true); }}
                sx={{
                  height: 56,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  color: 'text.primary',
                  fontWeight: 500,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                  px: 2,
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                  }
                }}
              >
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  {selectedLocation ? (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {locations.find(l => l.id === selectedLocation)?.name || 'Unknown Location'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      All Locations
                    </Typography>
                  )}
                </Box>
              </Button>
            </Box>
          </Grid>
          {!isProduct && (
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '0.9rem'
                }}>
                  {config.displayName.slice(0, -1)}
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { setDialogSelectedItem(selectedItem); setItemFilterDialogOpen(true); }}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    color: 'text.primary',
                    fontWeight: 500,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    px: 2,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {selectedItem ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {aggregatedData.find(i => i.item_id === selectedItem)?.item_name || `Unknown ${config.displayName.slice(0, -1)}`}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        All {config.displayName}
                      </Typography>
                    )}
                  </Box>
                </Button>
              </Box>
            </Grid>
          )}
          {isProduct ? (
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '0.9rem'
                }}>
                  Brand
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenBrandFilterDialog}
                  sx={{
                    justifyContent: 'space-between',
                    textAlign: 'left',
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    color: 'text.primary',
                    fontWeight: 500,
                    px: 2,
                    textTransform: 'none',
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                  endIcon={<ExpandMoreIcon />}
                >
                  {getSelectedBrandName()}
                </Button>
              </Box>
            </Grid>
          ) : (
            <Grid item xs={12} md={3}>
              <Box>
                <Typography variant="body2" sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '0.9rem'
                }}>
                  Vendor
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => { setDialogSelectedVendor(selectedSupplier); setVendorFilterDialogOpen(true); }}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: 'white',
                    color: 'text.primary',
                    fontWeight: 500,
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    px: 2,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box sx={{ textAlign: 'left', flex: 1 }}>
                    {selectedSupplier ? (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {suppliers.find(s => s.id === selectedSupplier)?.name || 'Unknown Vendor'}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        All Vendors
                      </Typography>
                    )}
                  </Box>
                </Button>
              </Box>
            </Grid>
          )}
          {isProduct && (
            <Grid item xs={12} md={3}>
              <Box sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                textAlign: 'center',
                cursor: 'default',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.1rem' }}>
                  {filteredInventory.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', fontWeight: 500, fontSize: '0.9rem' }}>
                  Products Found
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Table with pagination */}
      {isProduct ? (
        /* ── Product inventory: grouped by brand ── */
        <Box>
          {organizedByBrand.length === 0 ? (
            <Paper sx={{ textAlign: 'center', py: 8, px: 4, borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                No product inventory found
              </Typography>
            </Paper>
          ) : (
            organizedByBrand.map((brandGroup, brandIndex) => (
              <Box key={brandGroup.brand.id} sx={{ mb: 4 }}>
                {/* Brand Header */}
                <Box sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                  }
                }}>
                  <Box>
                    <Typography variant="h3" sx={{
                      fontWeight: 400,
                      color: 'text.primary',
                      fontSize: '2.2rem',
                      textAlign: 'left',
                      mb: 0.75
                    }}>
                      {brandGroup.brand.name}
                    </Typography>
                    <Typography variant="h6" sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                      textAlign: 'left',
                      fontSize: '1.05rem'
                    }}>
                      {brandGroup.inventory.length > 0
                        ? `Showing ${((brandGroup.currentPage - 1) * brandGroup.currentPageSize) + 1} - ${Math.min(brandGroup.currentPage * brandGroup.currentPageSize, brandGroup.inventory.length)} of ${brandGroup.inventory.length} items`
                        : 'No items'}
                    </Typography>
                  </Box>
                  <Stack direction="column" spacing={0.5}>
                    <Tooltip title="Move Up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={brandIndex === 0}
                          onClick={() => handleBrandReorder(brandGroup.brand.id, 'up')}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={brandIndex === organizedByBrand.length - 1}
                          onClick={() => handleBrandReorder(brandGroup.brand.id, 'down')}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>

                {/* Product Table for this brand */}
                <Paper sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={brandGroup.inventory.length}
                      rowsPerPage={brandGroup.currentPageSize}
                      page={brandGroup.currentPage - 1}
                      onPageChange={(event, newPage) => handleBrandPageChange(brandGroup.brand.id, newPage + 1)}
                      onRowsPerPageChange={(event) => handleBrandRowsPerPageChange(brandGroup.brand.id, event)}
                    />
                  </Box>
                  <ProductTable
                    data={brandGroup.paginatedInventory}
                    config={config}
                    inventoryType={inventoryType}
                    expandable
                    onRefresh={refresh}
                    noContainerBorder
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                  <Box sx={{
                    borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    borderRadius: '0 0 12px 12px'
                  }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component="div"
                      count={brandGroup.inventory.length}
                      rowsPerPage={brandGroup.currentPageSize}
                      page={brandGroup.currentPage - 1}
                      onPageChange={(event, newPage) => handleBrandPageChange(brandGroup.brand.id, newPage + 1)}
                      onRowsPerPageChange={(event) => handleBrandRowsPerPageChange(brandGroup.brand.id, event)}
                      sx={{
                        px: 3,
                        py: 2,
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          color: theme.palette.text.primary
                        },
                        '& .MuiTablePagination-select': {
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          background: 'white',
                          '&:hover': {
                            border: `1px solid ${theme.palette.grey[600]}`,
                            bgcolor: 'white'
                          }
                        },
                        '& .MuiTablePagination-actions': {
                          '& .MuiIconButton-root': {
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            margin: '0 2px',
                            background: 'white',
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            '&:hover': {
                              border: `1px solid ${theme.palette.grey[600]}`,
                              bgcolor: 'white'
                            },
                            '&.Mui-disabled': {
                              background: alpha(theme.palette.grey[300], 0.5),
                              color: theme.palette.grey[500],
                              border: `1px solid ${theme.palette.grey[300]}`
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            ))
          )}
        </Box>
      ) : (
        /* ── Non-product inventory: single flat table ── */
        <Paper sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          }
        }}>
          <Box sx={{ px: 1, py: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <PaginationController
              count={sortedInventory.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
          {inventoryType === 'chemicals' ? (
            <ChemicalTable
              data={paginatedInventory}
              config={config}
              inventoryType={inventoryType}
              expandable
              onRefresh={refresh}
              noContainerBorder
              canEdit={canEdit}
              canDelete={canDelete}
              primaryLocationId={primaryLocationId}
            />
          ) : (
            <InventoryTable
              data={paginatedInventory}
              config={config}
              inventoryType={inventoryType}
              expandable
              onRefresh={refresh}
              noContainerBorder
              canEdit={canEdit}
              canDelete={canDelete}
            />
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6, px: 3 }}>
            <Paper sx={{
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden'
            }}>
              <PaginationController
                count={sortedInventory.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Paper>
          </Box>
        </Paper>
      )}

      {/* Filter Dialogs */}
      <FilterDialogs
        locationFilterDialogOpen={locationFilterDialogOpen}
        setLocationFilterDialogOpen={setLocationFilterDialogOpen}
        locationSearchTerm={locationSearchTerm}
        setLocationSearchTerm={setLocationSearchTerm}
        locations={locations}
        dialogSelectedLocation={dialogSelectedLocation}
        setDialogSelectedLocation={setDialogSelectedLocation}
        onApplyLocationFilter={handleApplyLocationFilter}
        itemFilterDialogOpen={itemFilterDialogOpen}
        setItemFilterDialogOpen={setItemFilterDialogOpen}
        itemSearchTerm={itemSearchTerm}
        setItemSearchTerm={setItemSearchTerm}
        items={aggregatedData}
        dialogSelectedItem={dialogSelectedItem}
        setDialogSelectedItem={setDialogSelectedItem}
        onApplyItemFilter={handleApplyItemFilter}
        itemLabel={config.displayName}
        vendorFilterDialogOpen={vendorFilterDialogOpen}
        setVendorFilterDialogOpen={setVendorFilterDialogOpen}
        vendorSearchTerm={vendorSearchTerm}
        setVendorSearchTerm={setVendorSearchTerm}
        suppliers={suppliers}
        dialogSelectedVendor={dialogSelectedVendor}
        setDialogSelectedVendor={setDialogSelectedVendor}
        onApplyVendorFilter={handleApplyVendorFilter}
      />

      {/* Adjustments Selection Dialog */}
      <InventoryAdjustments
        open={adjustmentsDialogOpen}
        onClose={() => setAdjustmentsDialogOpen(false)}
        onSelectAdd={() => {
          setAdjustmentsDialogOpen(false);
          setAddStepperOpen(true);
        }}
        onSelectTransfer={() => {
          setAdjustmentsDialogOpen(false);
          setTransferStepperOpen(true);
        }}
        onSelectUsage={() => {
          setAdjustmentsDialogOpen(false);
          setUsageStepperOpen(true);
        }}
        inventoryType={inventoryType}
      />

      {/* Add Stock Stepper */}
      <AddInventoryStepper
        open={addStepperOpen}
        onClose={() => setAddStepperOpen(false)}
        onBack={() => { setAddStepperOpen(false); setAdjustmentsDialogOpen(true); }}
        inventoryType={inventoryType}
        companyId={companyId}
        items={items || []}
        locations={locations || []}
        suppliers={suppliers || []}
        onSuccess={(message, type = 'success') => {
          onShowSnackbar?.(message, type);
          refresh();
        }}
      />

      {/* Transfer Stepper */}
      <TransferInventoryStepper
        open={transferStepperOpen}
        onClose={() => setTransferStepperOpen(false)}
        onBack={() => { setTransferStepperOpen(false); setAdjustmentsDialogOpen(true); }}
        inventoryType={inventoryType}
        companyId={companyId}
        onSuccess={(message) => {
          onShowSnackbar?.(message, 'success');
          refresh();
        }}
      />

      {/* Usage Stepper */}
      <UsageInventoryStepper
        open={usageStepperOpen}
        onClose={() => setUsageStepperOpen(false)}
        onBack={() => { setUsageStepperOpen(false); setAdjustmentsDialogOpen(true); }}
        inventoryType={inventoryType}
        companyId={companyId}
        onSuccess={(message) => {
          onShowSnackbar?.(message, 'success');
          refresh();
        }}
      />
    </Box>

    {/* Floating Action Button */}
    {canEdit && createPortal(
      <Fab
        color="primary"
        aria-label="manage inventory"
        onClick={() => setAdjustmentsDialogOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          width: 80,
          height: 80,
          boxShadow: theme.shadows[6],
          zIndex: 9999,
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: theme.shadows[8]
          }
        }}
      >
        <TuneIcon sx={{ fontSize: 28 }} />
      </Fab>,
      document.body
    )}

    {/* Export Report Dialog */}
    <Dialog
      open={exportReportDialogOpen}
      onClose={() => setExportReportDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        p: 3,
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <PrintIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Export Report
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary', fontWeight: 500 }}>
          Choose the report format you want to export:
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            label="Report Type"
            onChange={(e) => setReportType(e.target.value)}
          >
            <MenuItem value="summary">Summary (Total Quantities Only)</MenuItem>
            <MenuItem value="detailed">Detailed (With Lot/Batch Breakdown)</MenuItem>
          </Select>
        </FormControl>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {reportType === 'summary' 
            ? `This report will show aggregated totals for each ${config.displayName.toLowerCase()} type and location.`
            : `This report will include a detailed breakdown of all lot numbers and batch numbers for each ${config.displayName.slice(0, -1).toLowerCase()}.`}
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
        <Button
          onClick={() => setExportReportDialogOpen(false)}
          startIcon={<CancelIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={async () => {
            try {
              await exportInventoryReport(inventoryType, companyId, sortedInventory, config, reportType);
              setExportReportDialogOpen(false);
            } catch (error) {
              console.error('Error exporting report:', error);
            }
          }}
          startIcon={<PrintIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 500,
            bgcolor: 'primary.main',
            boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': {
              bgcolor: 'primary.dark',
              transform: 'translateY(-1px)',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
            }
          }}
        >
          Generate Report
        </Button>
      </DialogActions>
    </Dialog>

    {/* Brand Filter Dialog (products only) */}
    <Dialog
      open={brandFilterDialogOpen}
      onClose={handleCloseBrandFilterDialog}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 4,
          height: '70vh',
          margin: 'auto',
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
        }
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontSize: '1.25rem',
        fontWeight: 500,
        color: 'text.primary',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
          Select Brand
        </Typography>
        <IconButton onClick={handleCloseBrandFilterDialog} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
          <TextField
            fullWidth
            placeholder="Search brands..."
            value={brandFilterSearchTerm}
            onChange={(e) => setBrandFilterSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: 'white',
                '&:hover': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                },
                '&.Mui-focused': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                }
              }
            }}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              }
            }}
          />
        </Box>
        <Box sx={{
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* All Brands option */}
              <TableRow
                hover
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'grey.50',
                    transform: 'translateX(4px)',
                    boxShadow: 'inset 4px 0 0 black'
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    py: 2, pl: 3, pr: 3
                  }
                }}
              >
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      All Brands
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Show products from all brands
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleDialogBrandFilterSelect('all')}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      px: 2,
                      py: 1,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      bgcolor: dialogSelectedBrandFilter === 'all' ? 'black' : 'transparent',
                      color: dialogSelectedBrandFilter === 'all' ? 'white' : 'inherit',
                      transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                      '&:hover': {
                        border: `1px solid ${theme.palette.primary.main}`,
                        bgcolor: dialogSelectedBrandFilter === 'all' ? 'black' : 'transparent',
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  >
                    Select
                  </Button>
                </TableCell>
              </TableRow>
              {getFilteredBrandsForFilter().map((brand) => (
                <TableRow
                  key={brand.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'grey.50',
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                      py: 2, pl: 3, pr: 3
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                      {brand.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleDialogBrandFilterSelect(brand)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        px: 2,
                        py: 1,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        bgcolor: dialogSelectedBrandFilter?.id === brand.id ? 'black' : 'transparent',
                        color: dialogSelectedBrandFilter?.id === brand.id ? 'white' : 'inherit',
                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          bgcolor: dialogSelectedBrandFilter?.id === brand.id ? 'black' : 'transparent',
                          transform: 'translateY(-1px)',
                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {getFilteredBrandsForFilter().length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {brandFilterSearchTerm ? 'No brands found matching your search.' : 'No brands available.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </DialogContent>
      <DialogActions sx={{
        p: 3,
        gap: 2,
        borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
        bgcolor: alpha(theme.palette.grey[50], 0.5),
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={() => window.open(`/company/${cid}/brands`, '_blank')}
          variant="outlined"
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontSize: '0.9rem',
            fontWeight: 500,
            textTransform: 'none',
            border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
            color: 'success.main',
            '&:hover': {
              border: `1px solid ${theme.palette.success.main}`,
              bgcolor: alpha(theme.palette.success.main, 0.1),
              transform: 'translateY(-1px)'
            }
          }}
        >
          Add Brand
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={handleCloseBrandFilterDialog}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              '&:hover': {
                border: `1px solid ${theme.palette.grey[600]}`,
                transform: 'translateY(-1px)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmBrandFilterSelection}
            variant="contained"
            disabled={!brandSelectionMade}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 500,
              textTransform: 'none',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                transform: 'translateY(-1px)',
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
              },
              '&:disabled': {
                background: alpha(theme.palette.grey[400], 0.5),
                color: alpha(theme.palette.grey[600], 0.7)
              }
            }}
          >
            Confirm Selection
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    </Fragment>
  );
}
